import React, { useEffect, useRef, useState } from "react";
import moment from "moment";

export const TOTAL_SEGMENTS = 10;

// The 9 steps a Strategy Canvas engagement moves through. Index (0-based) + 1
// lines up with the "filled" count on each SME row, i.e. filled = 3 means
// steps 1-3 have been reached and STEPS[2] ("Strategy Canvas V1 started") is
// the current step.
export const STEPS = [
  // Named for the steps the CEO sees (Interview, AI final review), in
  // sentence case. Order and count unchanged — the API's step index maps
  // onto this array.
  "Setup started",
  "Setup completed",
  "Interview started",
  "Interview completed (v1)",
  "AI final review started",
  "AI final review completed (v2)",
  "Ready for your review",
  "Live review scheduled",
  "Live review started",
  "Strategy Canvas locked",
];

// Turns an API block_key (e.g. "true_north_metric", "bhag") into display
// copy ("True North Metric", "BHAG") for the Business Context tab.
const BLOCK_KEY_ACRONYMS = ["bhag", "uvp", "mpvv", "kpi"];

export const formatBlockKey = (blockKey = "") =>
  blockKey
    .split("_")
    .map((word) =>
      BLOCK_KEY_ACRONYMS.includes(word)
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");

export const ArrowRightIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.917 7H11.083"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M7.583 3.5L11.083 7L7.583 10.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DownloadIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 1.75V8.75M7 8.75L9.625 6.125M7 8.75L4.375 6.125"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.333 10.5V11.083C2.333 11.727 2.856 12.25 3.5 12.25H10.5C11.144 12.25 11.667 11.727 11.667 11.083V10.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ExternalLinkIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.333 9.333H9.333A2.667 2.667 0 0 0 6.667 12v10.667A2.667 2.667 0 0 0 9.333 25.333H20A2.667 2.667 0 0 0 22.667 22.667v-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.667 6.667h6.666m0 0v6.667m0-6.667L14.667 17.333"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CopyIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="6"
      y="6"
      width="8"
      height="8"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <path
      d="M3.333 10H2.667A1.333 1.333 0 0 1 1.333 8.667V2.667a1.333 1.333 0 0 1 1.334-1.334h6A1.333 1.333 0 0 1 10 2.667V3.333"
      stroke="currentColor"
      strokeWidth="1.2"
    />
  </svg>
);

export const CheckCircleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" r="8" fill="#0CA789" />
    <path
      d="M4.8 8.2L6.8 10.2L11.2 5.8"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Icons added for the Strategy Canvas Review / Canvas Mode feature ---
// Placeholder inline SVGs (per request) - swap for the real Figma icon
// exports whenever they're available.

export const InfoIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M8 7.333V11.333"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx="8" cy="5" r="0.75" fill="currentColor" />
  </svg>
);

// Inverted InfoIcon — same circle, bar and dot flipped — so a validation
// message reads as a warning at a glance without introducing a second shape
// language next to the info affordance it sits near.
export const WarningIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="M8 4.5V9"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <circle cx="8" cy="11.25" r="0.8" fill="currentColor" />
  </svg>
);

export const CommentIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 3.333A1.333 1.333 0 0 1 3.333 2h9.334A1.333 1.333 0 0 1 14 3.333V9.333a1.333 1.333 0 0 1-1.333 1.334H6l-3 2.666v-2.666H3.333A1.333 1.333 0 0 1 2 9.333V3.333Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

export const TrashIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.667 4.667h10.666M6.667 7.333v3.334M9.333 7.333v3.334M3.333 4.667l.667 7.333A1.333 1.333 0 0 0 5.328 13.333h5.344a1.333 1.333 0 0 0 1.328-1.333l.667-7.333M6 4.667V2.667A0.667 0.667 0 0 1 6.667 2h2.666a0.667 0.667 0 0 1 0.667 0.667v2"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const EditIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.333 2.667a1.333 1.333 0 0 1 1.886 1.886L5.662 12.11l-2.44.543.543-2.44 7.568-7.546Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CheckIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.333 8.333 6.5 11.5l6.167-6.833"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CloseIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13 3L3 13M3 3L13 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const LockIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="3.5"
      y="7.333"
      width="9"
      height="6"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path
      d="M5.333 7.333V5.333A2.667 2.667 0 0 1 8 2.667A2.667 2.667 0 0 1 10.667 5.333V7.333"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

export const DiscussIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 4.667V8.667M8 11.333h.007"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

export const ChevronRightIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path
      d="M5 3l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChevronUpIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 10L8 6L12 10"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Row-level status dot (unchecked ring / accepted green / discuss orange).
export const StatusDotIcon = ({ status, size = 16 }) => {
  const fillByStatus = {
    accepted: "#0CA789",
    to_discuss: "#FFA95C",
  };
  const fill = fillByStatus[status];
  if (fill) {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="8" fill={fill} />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" stroke="#E6E4E5" strokeWidth="1.4" />
    </svg>
  );
};

// Shared accepted/to-discuss (or discussed/to-discuss) pill - repeated
// across ReviewRow, StrategyCanvasReviewMode's Review Summary cards, and
// StrategyCanvasLiveReviewWaiting's LiveReviewRow/AgendaCard. Each caller
// still owns its own SCSS class names (visual details differ slightly per
// context), so this only centralizes the icon + label swap logic.
export const StatusPill = ({
  accepted,
  className,
  acceptedClassName,
  discussClassName,
  acceptedLabel = "Accepted",
  discussLabel = "To discuss",
}) => (
  <span
    className={`${className} ${accepted ? acceptedClassName : discussClassName}`}
  >
    {accepted ? <CheckIcon size={12} /> : <DiscussIcon size={12} />}
    {accepted ? acceptedLabel : discussLabel}
  </span>
);

// A single comment, in either display or edit mode. Edit state
// (isEditing/draft/saving) lives here, per-item, rather than being lifted to
// the three CommentList callers - each comment edits independently, and the
// callers only need to supply onEdit (persist) the same way they already
// supply onDelete.
//
// `onEdit` is optional - omit it to render delete without edit. Added for
// Execution Blueprint, whose comment endpoints have no PUT (COACH-API.md
// §4): before this, `!readOnly` rendered edit and delete together
// unconditionally, so there was no way to keep delete while dropping edit -
// omitting the prop meant a live edit button that threw on Save. Existing
// callers are unaffected; all three already pass `onEdit`.
const CommentItem = ({ comment, onDelete, onEdit, deletingId, readOnly, badge }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(comment.comment_text);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!isEditing || !textareaRef.current) return;
    const element = textareaRef.current;
    element.focus();
    const end = element.value.length;
    element.setSelectionRange(end, end);
  }, [isEditing]);

  const startEdit = () => {
    setDraft(comment.comment_text);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(comment.comment_text);
    setIsEditing(false);
  };

  const trimmedDraft = draft.trim();
  const isUnchanged = trimmedDraft === comment.comment_text.trim();

  const saveEdit = async () => {
    if (!trimmedDraft || isUnchanged || saving) return;
    setSaving(true);
    const saved = await onEdit(comment.coach_comment_id, trimmedDraft);
    setSaving(false);
    if (saved) setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="rr-comment-card rr-comment-card--editing">
        <textarea
          ref={textareaRef}
          className="rr-comment-edit-textarea"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") cancelEdit();
          }}
          disabled={saving}
          rows={3}
        />
        <div className="rr-comment-edit-actions">
          <button
            type="button"
            className="rr-comment-cancel-btn"
            onClick={cancelEdit}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rr-save-comment-btn"
            onClick={saveEdit}
            disabled={saving || !trimmedDraft || isUnchanged}
          >
            {saving ? "Saving…" : "Save"}
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rr-comment-card">
      <span className="rr-comment-card-icon">
        <CommentIcon size={14} />
      </span>

      <div className="rr-comment-card-main">
        <div className="rr-comment-card-top">
          {/* Block and section comments sit directly under the thing they're
              about, so the card needs no label. An overall assessment has
              nothing above it naming it, so its caller passes a badge. */}
          {badge && <span className="rr-comment-card-badge">{badge}</span>}
          <span className="rr-comment-card-who">Your comment for the CEO</span>
          {comment.created_at && (
            <>
              <span className="rr-comment-card-dot" />
              <span className="rr-comment-card-when">
                {moment(comment.created_at).format("MMM D, h:mm A")}
              </span>
            </>
          )}
        </div>
        <p className="rr-comment-card-text">{comment.comment_text}</p>
      </div>

      {!readOnly && (
        <span className="rr-comment-item-actions">
          {onEdit && (
            <button
              type="button"
              className="rr-comment-edit"
              onClick={startEdit}
              aria-label="Edit comment"
            >
              <EditIcon />
            </button>
          )}
          <button
            type="button"
            className="rr-comment-delete"
            onClick={() => onDelete(comment.coach_comment_id)}
            disabled={deletingId === comment.coach_comment_id}
            aria-label="Delete comment"
          >
            <TrashIcon />
          </button>
        </span>
      )}
    </div>
  );
};

// Shared comment list - one card per comment, edit/delete revealed on hover.
// Block, section and overall comments all render through this and look
// identical; callers only vary the list wrapper class, since spacing around
// the list differs slightly per context.
export const CommentList = ({
  comments,
  onDelete,
  onEdit,
  deletingId,
  readOnly,
  className = "rr-comment-list",
  badge,
}) => (
  <div className={className}>
    {comments.map((comment) => (
      <CommentItem
        key={comment.coach_comment_id}
        comment={comment}
        onDelete={onDelete}
        onEdit={onEdit}
        deletingId={deletingId}
        readOnly={readOnly}
        badge={badge}
      />
    ))}
  </div>
);

// Ids for the aria-describedby link between a field and its error. React 17
// has no useId, and several composers can be open at once, so a module-level
// counter keeps them unique per instance.
let composerSeq = 0;

// Shared comment composer - textarea, validation, error state and Save button.
// Block, section and overall comments all write through this, which is what
// keeps the empty-draft behaviour identical everywhere; before it existed each
// one hit Save and silently did nothing, indistinguishable from a dead button.
//
// Validation lives here rather than in the callers because the rule is the
// same in all three: a comment has to have words in it. `onSave` is only
// called once that holds, so callers never handle the empty case.
export const CommentComposer = ({
  value,
  onChange,
  onSave,
  saving,
  placeholder,
  className = "rr-comment-composer",
  rows = 3,
  emptyMessage = "Please write a comment before saving.",
  // Every caller mounts this in response to "Add a comment for CEO", so the
  // click and the intent to type are the same action - landing in the field
  // saves a second click every single time. Opt out if a future caller ever
  // renders it inline on load, where grabbing focus would be rude.
  autoFocus = true,
}) => {
  const [error, setError] = useState("");
  const textareaRef = useRef(null);
  const errorIdRef = useRef(null);
  if (errorIdRef.current === null) {
    composerSeq += 1;
    errorIdRef.current = `cc-error-${composerSeq}`;
  }

  // Same treatment the edit-an-existing-comment flow already uses: focus, then
  // put the caret at the end rather than selecting everything, so a preserved
  // draft can be continued instead of being overwritten by the next keystroke.
  useEffect(() => {
    if (!autoFocus || !textareaRef.current) return;
    const element = textareaRef.current;
    element.focus();
    const end = element.value.length;
    element.setSelectionRange(end, end);
    // Mount-only: refocusing on every value change would fight the user if
    // they ever click away mid-sentence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const handleChange = (event) => {
    // Clear on the first keystroke - leaving it up while they're fixing it
    // reads as though typing isn't helping.
    if (error) setError("");
    onChange(event.target.value);
  };

  const handleSave = () => {
    if (saving) return;
    if (!value.trim()) {
      setError(emptyMessage);
      return;
    }
    setError("");
    onSave();
  };

  return (
    <div className={`${className}${error ? " rm-composer--error" : ""}`}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        aria-invalid={!!error}
        aria-describedby={error ? errorIdRef.current : undefined}
      />
      {/* role="alert" so it's announced, not just seen - the button that
          triggers it sits below the field, outside where a screen reader
          would land next. */}
      {error && (
        <span className="rm-comment-error" id={errorIdRef.current} role="alert">
          <WarningIcon size={13} />
          {error}
        </span>
      )}
      <button
        className="rr-save-comment-btn"
        onClick={handleSave}
        disabled={saving}
      >
        Save comment
        <ArrowRightIcon />
      </button>
    </div>
  );
};

export const ProgressBar = ({ filled }) => {
  const isComplete = filled >= TOTAL_SEGMENTS;
  return (
    <div className="sc-progress">
      {Array.from({ length: TOTAL_SEGMENTS }).map((_, index) => {
        const segmentIndex = index + 1;
        const isFilled = segmentIndex <= filled;
        const isCurrent = !isComplete && segmentIndex === filled + 1;
        return (
          <div
            key={index}
            className={`sc-progress-segment ${isFilled ? "filled" : ""} ${
              isCurrent ? "current" : ""
            }`}
          />
        );
      })}
    </div>
  );
};
